/**
 * Bloom - the A Whole Family Matter companion.
 *
 * The mood check-in's flower, with a personality. Six poses, each with a small
 * idle movement; motion stops under reduce-motion (OS or Settings → Appearance).
 *
 * Tone rule: Bloom belongs in onboarding, empty states, small wins and loading.
 * It never appears in grief, legal, capacity, payment-problem or security
 * moments (recording a passing, heir offers, findings, dissolving a team,
 * account deletion, legal pages, security emails).
 *
 *   <Bloom pose="hello" size={72} />
 */

import React, { useId } from 'react';
import { softStar } from '../MoodFace';
import './Bloom.css';

const BODY = softStar(80, 84, 64, 47, 10);
const F = '#2d1040';

const Leaf = ({ x, y, rot, className = '' }) => (
  <g transform={`translate(${x} ${y}) rotate(${rot})`}>
    <g className={className}>
      <path d="M0 0 Q14 -16 30 -10 Q20 6 0 0 Z" fill="#7fcfa6" />
      <path d="M2 -1 Q14 -7 26 -9" stroke="#4fae84" strokeWidth="1.4" fill="none" strokeLinecap="round" />
    </g>
  </g>
);

const Cheeks = () => (
  <>
    <ellipse cx="54" cy="92" rx="7" ry="4.5" fill="#f7a8c8" opacity=".55" />
    <ellipse cx="106" cy="92" rx="7" ry="4.5" fill="#f7a8c8" opacity=".55" />
  </>
);

const EYES = {
  open: (
    <g className="bloom__blink">
      <ellipse cx="64" cy="80" rx="5" ry="5.6" fill={F} />
      <ellipse cx="96" cy="80" rx="5" ry="5.6" fill={F} />
      <circle cx="66" cy="78" r="1.6" fill="#fff" />
      <circle cx="98" cy="78" r="1.6" fill="#fff" />
    </g>
  ),
  up: (
    <g className="bloom__blink">
      <ellipse cx="67" cy="77" rx="5" ry="5.6" fill={F} />
      <ellipse cx="99" cy="77" rx="5" ry="5.6" fill={F} />
      <circle cx="69" cy="74.5" r="1.7" fill="#fff" />
      <circle cx="101" cy="74.5" r="1.7" fill="#fff" />
    </g>
  ),
  happy: (
    <>
      <path d="M57 80 Q64 73 71 80" stroke={F} strokeWidth="3.2" strokeLinecap="round" fill="none" />
      <path d="M89 80 Q96 73 103 80" stroke={F} strokeWidth="3.2" strokeLinecap="round" fill="none" />
    </>
  ),
  closed: (
    <>
      <path d="M57 79 Q64 84 71 79" stroke={F} strokeWidth="3" strokeLinecap="round" fill="none" />
      <path d="M89 79 Q96 84 103 79" stroke={F} strokeWidth="3" strokeLinecap="round" fill="none" />
    </>
  ),
  soft: (
    <>
      <g className="bloom__blink">
        <ellipse cx="64" cy="81" rx="4.6" ry="4.4" fill={F} />
        <ellipse cx="96" cy="81" rx="4.6" ry="4.4" fill={F} />
      </g>
      <path d="M57 72 Q64 70 71 73" stroke={F} strokeWidth="2.2" strokeLinecap="round" fill="none" opacity=".7" />
      <path d="M89 73 Q96 70 103 72" stroke={F} strokeWidth="2.2" strokeLinecap="round" fill="none" opacity=".7" />
    </>
  ),
};

const MOUTHS = {
  smile: <path d="M68 96 Q80 106 92 96" stroke={F} strokeWidth="3.2" strokeLinecap="round" fill="none" />,
  open: (
    <>
      <path d="M68 94 Q80 110 92 94 Z" fill={F} />
      <path d="M73 100 Q80 105 87 100" fill="#f08bb7" />
    </>
  ),
  small: <path d="M73 98 Q80 102 87 98" stroke={F} strokeWidth="3" strokeLinecap="round" fill="none" />,
  hmm: <path d="M71 98 Q79 96 88 99" stroke={F} strokeWidth="3" strokeLinecap="round" fill="none" />,
  sleep: <ellipse cx="80" cy="99" rx="4" ry="3" fill={F} />,
};

const POSES = {
  hello: {
    motion: 'bob',
    back: (<><Leaf x={132} y={92} rot={-40} className="bloom__wave" /><Leaf x={28} y={110} rot={200} /></>),
    face: (<>{EYES.open}{MOUTHS.smile}<Cheeks /></>),
  },
  thinking: {
    motion: 'sway',
    back: (
      <g className="bloom__dots">
        <circle cx="128" cy="34" r="4" fill="#c9a6e0" />
        <circle cx="141" cy="22" r="5" fill="#c9a6e0" />
        <circle cx="156" cy="10" r="6" fill="#c9a6e0" />
      </g>
    ),
    face: (<>{EYES.up}{MOUTHS.hmm}<Cheeks /></>),
  },
  listening: {
    motion: 'breathe',
    back: (
      <>
        <path d="M140 70 Q148 84 140 98" stroke="#d39af5" strokeWidth="3" fill="none" strokeLinecap="round" opacity=".8" />
        <path d="M150 62 Q162 84 150 106" stroke="#d39af5" strokeWidth="3" fill="none" strokeLinecap="round" opacity=".5" />
      </>
    ),
    face: (<>{EYES.closed}{MOUTHS.small}<Cheeks /></>),
  },
  celebrating: {
    motion: 'bob',
    back: (
      <>
        <g className="bloom__pop">
          <path d="M20 30 l6 -10 l6 10 z" fill="#f9b4dc" />
          <circle cx="142" cy="28" r="5" fill="#ffd36e" />
          <rect x="132" y="120" width="9" height="9" rx="2" fill="#a78bfa" transform="rotate(20 136 124)" />
          <circle cx="18" cy="122" r="4" fill="#7fcfa6" />
        </g>
        <Leaf x={130} y={70} rot={-70} />
        <Leaf x={30} y={70} rot={250} />
      </>
    ),
    face: (<>{EYES.happy}{MOUTHS.open}<Cheeks /></>),
  },
  resting: {
    motion: 'breathe',
    back: (
      <>
        <text x="124" y="40" fontFamily="sans-serif" fontWeight="600" fontSize="16" fill="#c9a6e0">z</text>
        <text x="138" y="24" fontFamily="sans-serif" fontWeight="600" fontSize="12" fill="#c9a6e0" opacity=".7">z</text>
      </>
    ),
    face: (<>{EYES.closed}{MOUTHS.sleep}<Cheeks /></>),
  },
  withyou: {
    motion: 'breathe',
    back: (<><Leaf x={112} y={132} rot={-150} /><Leaf x={48} y={132} rot={330} /></>),
    face: (<>{EYES.soft}{MOUTHS.small}</>),
    front: (
      <g className="bloom__heart">
        <path d="M80 146 C 68 138, 64 128, 72 124 C 76 122, 80 125, 80 127 C 80 125, 84 122, 88 124 C 96 128, 92 138, 80 146 Z" fill="#f08bb7" stroke="#fff" strokeWidth="2" />
      </g>
    ),
  },
};

const LABELS = {
  hello: 'Bloom waving hello',
  thinking: 'Bloom thinking',
  listening: 'Bloom listening',
  celebrating: 'Bloom celebrating',
  resting: 'Bloom resting',
  withyou: 'Bloom, here with you',
};

export default function Bloom({ pose = 'hello', size = 96, decorative = true, className = '' }) {
  const p = POSES[pose] || POSES.hello;
  const id = useId().replace(/:/g, '');
  return (
    <svg
      className={`bloom bloom--${p.motion} ${className}`}
      viewBox="0 0 160 160"
      width={size}
      height={size}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? 'true' : undefined}
      aria-label={decorative ? undefined : LABELS[pose]}
      focusable="false"
    >
      <defs>
        <linearGradient id={`${id}g`} x1="30" y1="20" x2="130" y2="150" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#f9b4dc" />
          <stop offset="1" stopColor="#d39af5" />
        </linearGradient>
        <radialGradient id={`${id}h`} cx="0.35" cy="0.3" r="0.6">
          <stop offset="0" stopColor="#fff" stopOpacity=".55" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="80" cy="152" rx="40" ry="5" fill="#2d1040" opacity=".08" />
      <g className="bloom__body">
        {p.back}
        <path d={BODY} fill={`url(#${id}g)`} />
        <path d={BODY} fill={`url(#${id}h)`} />
        {p.face}
        {p.front}
      </g>
    </svg>
  );
}
